import React, { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';

import LandingPage from './components/LandingPage.js';            // now public at /welcome
import Dashboard from './components/Dashboard.js';
import Login from './components/Login.jsx';
import CognitoCallback from './components/CognitoCallback.jsx';

import MarketingPanel from './components/MarketingPanel.jsx';

import CatalogPanel from './components/catalog/CatalogPanel.jsx';
import CatalogShowcase from './components/CatalogShowcase.jsx';
import LibraryCatalog from './components/LibraryCatalog.jsx';
import SimpleCatalogLibrary from './components/SimpleCatalogLibrary.jsx';

import AnalyticsPanel from './components/AnalyticsPanel.jsx';
import SpotifyModule from './components/SpotifyModule.js';

import ViralAutomationPanel from './components/ViralAutomationPanel.jsx';
import IntelligentPitchPanel from './components/IntelligentPitchPanel.jsx';

import BuzzPage from './pages/BuzzPage.js';
import PitchPage from './pages/PitchPage.jsx';
import Admin from './pages/Admin.jsx';
import ContactForm from './components/ContactForm.jsx';
import About from './pages/About.jsx';

import Navbar from './components/navigation/Navbar.jsx';
import { useAuth } from './context/AuthContext.js';
import ANALYTICS from './utils/analytics.js';
import './App.css';

// Top-level layout to avoid repeating Navbar
function Shell({ isAuthenticated, username, onSignOut }) {
  return (
    <>
      <Navbar isAuthenticated={isAuthenticated} username={username} onSignOut={onSignOut} />
      <div className="page-wrap"><Outlet /></div>
    </>
  );
}

// Simple auth gate
function ProtectedRoute({ isAuthenticated, redirectTo = '/login' }) {
  if (!isAuthenticated) return <Navigate to={redirectTo} replace />;
  return <Outlet />;
}

// Optional: section wrappers (handy if you add section-specific chrome later)
const Section = ({ children }) => <>{children || <Outlet />}</>;

export default function App() {
  const { isAuthenticated, user, username, signOut, loading } = useAuth();

  useEffect(() => { try { ANALYTICS && ANALYTICS(); } catch (_) {} }, []);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading DecodedMusic...</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Routes>
        <Route element={<Shell isAuthenticated={isAuthenticated} username={username} onSignOut={signOut} />}>

          {/* -------- Public -------- */}
          <Route path="/" element={<Navigate to={isAuthenticated ? '/workspace' : '/welcome'} replace />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/login" element={isAuthenticated ? <Navigate to="/workspace" replace /> : <Login />} />
          <Route path="/callback" element={<CognitoCallback />} />
          <Route path="/buzz" element={<BuzzPage />} />
          <Route path="/pitch" element={<PitchPage />} />
          <Route path="/contact" element={<ContactForm />} />
          <Route path="/about" element={<About />} />
          {/* Public catalog routes */}
          <Route path="/library-catalog" element={<LibraryCatalog />} />
          <Route path="/simple-catalog" element={<SimpleCatalogLibrary />} />
          <Route path="/catalog" element={<CatalogPanel />} />
          <Route path="/catalog/showcase" element={<CatalogShowcase />} />

          {/* -------- Auth-only -------- */}
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
            {/* Workspace (your “home” for logged-in users) */}
            <Route path="/workspace" element={<Section />}>
              <Route index element={<Dashboard user={user} username={username} onSignOut={signOut} />} />
              <Route path="social" element={<MarketingPanel user={user} />} />
            </Route>

            {/* Catalog */}
            <Route path="/catalog" element={<Section />}>
              <Route index element={<CatalogPanel user={user} />} />
              <Route path="showcase" element={<CatalogShowcase />} />
              <Route path="library" element={<LibraryCatalog />} />
              <Route path="simple" element={<SimpleCatalogLibrary />} />
            </Route>

            {/* Insights */}
            <Route path="/insights" element={<Section />}>
              <Route index element={<AnalyticsPanel user={user} />} />
              <Route path="spotify" element={<SpotifyModule user={user} />} />
              {/* add /insights/deep etc. later if needed */}
            </Route>

            {/* Growth */}
            <Route path="/growth" element={<Section />}>
              <Route path="viral-automation" element={<ViralAutomationPanel />} />
              <Route path="intelligent-pitch" element={<IntelligentPitchPanel />} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<Admin />} />
          </Route>

          {/* -------- Legacy redirects so old links still work -------- */}
          <Route path="/dashboard" element={<Navigate to="/workspace" replace />} />
          <Route path="/social" element={<Navigate to="/workspace/social" replace />} />
          <Route path="/catalog-showcase" element={<Navigate to="/catalog/showcase" replace />} />
          <Route path="/analytics" element={<Navigate to="/insights" replace />} />
          <Route path="/spotify" element={<Navigate to="/insights/spotify" replace />} />
          <Route path="/marketing" element={<Navigate to="/workspace/social" replace />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to={isAuthenticated ? '/workspace' : '/welcome'} replace />} />
        </Route>
      </Routes>
    </div>
  );
}
